import {
  faFolderOpen,
  faListCheck,
  faPlus,
  faSearch,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";
import {
  GSListPage,
  GSInput,
  GSEmptyList,
  GSItemList,
} from "golfstatus_react_components";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMatch, useNavigate } from "react-router-dom";
import {
  selectCurrentTournament,
  selectLoading,
} from "../../../reducers/tournamentSlice";
import { Outlet } from "react-router-dom";

import "./tournament-rounds.scss";
import {
  getTournamentRounds,
  selectTournamentRounds,
  setCurrentRound,
  selectLoading as roundLoading,
  setCurrentRoundHoleAssignments,
} from "../../../reducers/roundSlice";
import TournamentRoundGroup from "./tournament-round-group";
import { getRoundGroups } from "../../../hooks/round/roundHooks";
import { usePreflighting } from "../../../hooks/flights/flightHooks";

const TournamentRounds = (props) => {
  const [name, setName] = useState("");

  const currentTournament = useSelector(selectCurrentTournament);
  const currentTournamentRounds = useSelector(selectTournamentRounds);

  const navigate = useNavigate();
  const match = useMatch("/:tournamentID/rounds/*");
  const dispatch = useDispatch();
  const isBase = match?.params?.["*"] === "";

  const tournamentID = match?.params?.tournamentID;

  useEffect(() => {
    if (currentTournament.id && isBase) {
      dispatch(getTournamentRounds(currentTournament?.id));
      dispatch(setCurrentRoundHoleAssignments([]));
    }
  }, [dispatch, currentTournament, isBase]);

  useEffect(() => {
    return () => {
      dispatch(setCurrentRoundHoleAssignments([]));
    };
  }, [dispatch]);

  const loading = useSelector(selectLoading);
  const tournamentRoundLoading = useSelector(roundLoading);

  const clearText = () => {
    setName("");
  };

  const createRound = (round) => {
    dispatch(setCurrentRound({}));
    navigate("new");
  };

  const teamCheckin = (round) => {
    dispatch(setCurrentRound({}));
    navigate("checkin");
  };

  const clearSearchFields = () => {
    setName("");
  };

  const printouts = () => {
    navigate("documents");
  };

  const getPageActions = () => {
    return [
      {
        id: 1,
        buttonTitle: "Add Round",
        type: "black mobile-icon",
        actionClick: createRound,
        actionIcon: faPlus,
      },
      {
        id: 1,
        buttonTitle: "Team Check In",
        type: "grey mobile-icon",
        actionClick: teamCheckin,
        actionIcon: faListCheck,
      },

      {
        id: 1,
        buttonTitle: "Documents",
        type: "light-grey mobile-icon",
        actionClick: printouts,
        actionIcon: faFolderOpen,
      },
    ];
  };

  const getEmptyListMessage = () => {
    return (
      <GSEmptyList
        title="Add & Manage Rounds"
        detail="Add new rounds and manage all round details in one spot."
        actions={[
          {
            title: "Add Round",
            buttonIcon: faPlus,
            onClick: createRound,
            type: "black",
          },
        ]}
      />
    );
  };

  const getEmptySearchMessage = () => {
    return (
      <GSEmptyList
        title="We couldn’t find the round you’re looking for…"
        detail="Try changing the search term or add a new round."
        actions={[
          {
            title: "Clear Search",
            onClick: clearSearchFields,
            type: "light-grey",
          },
          {
            title: "Add Round",
            onClick: createRound,
            buttonIcon: faPlus,
            type: "black",
          },
        ]}
      />
    );
  };

  const getFilteredRounds = () => {
    return currentTournamentRounds?.filter?.(
      (round) =>
        (round.roundName ?? "").includes?.(name) ||
        round.number === parseInt(name),
    );
  };

  const getRoundGroup = (group) => {
    return <TournamentRoundGroup group={group} roundSelected={selectRound} />;
  };

  const selectRound = (round) => {
    if (isSidePanelOpen()) {
      navigate(`/${match.params.tournamentID}/rounds`);
    } else {
      navigate(round.id);
    }
  };

  const isListLoading = () =>
    loading?.includes?.("getTournament") ||
    tournamentRoundLoading?.includes?.("getTournamentRounds");

  //each round line item header carries its own shadow. with nothing in the list
  //there are no headers, so the search bar keeps its built in shadow instead.
  //the rounds reload every time the page opens, so the list only counts as
  //empty once they're in, or the shadow flashes on while they load.
  const isListEmpty = () =>
    !isListLoading() && (getFilteredRounds()?.length ?? 0) === 0;

  const getItemList = () => {
    return (
      <GSItemList
        items={getRoundGroups(getFilteredRounds())}
        listItem={(group) => getRoundGroup(group)}
        type="vertical round-list"
        emptyMessage={
          name !== "" ? getEmptySearchMessage() : getEmptyListMessage()
        }
        loading={isListLoading()}
        loadingMainText="Loading Rounds"
      ></GSItemList>
    );
  };

  const searchChanged = (e) => {
    setName(e.target.value);
  };

  const getDefaultSearch = () => {
    return (
      <div className="round-search">
        <GSInput
          textValue={name}
          leftIcon={faSearch}
          rightIconClick={clearText}
          rightIcon={faTimesCircle}
          onChange={searchChanged}
          placeholder="Search by Round Number or Label…"
        ></GSInput>
      </div>
    );
  };

  const isSidePanelOpen = () => {
    return match?.params?.["*"] !== "";
  };

  const getRouter = () => {
    return <Outlet />;
  };

  const {showFlightingBanner, getBanner} = usePreflighting({tournamentID});

  const getListPage = (title) => {
    const page = {
      title: title,
      getDefaultSearch: getDefaultSearch,
      getPageActions: getPageActions,
      getItemList: getItemList,
      isSidePanelOpen: isSidePanelOpen,
      getRouter: getRouter,
      getBanner: showFlightingBanner ? getBanner : false,
    };

    return <GSListPage {...page} />;
  };

  return (
    //custom elements take the attribute name as given, so this is class, not className.
    <tournament-rounds class={isListEmpty() ? "empty-round-list" : ""}>
      {getListPage("Rounds & Scorecards")}
    </tournament-rounds>
  );
};

export default TournamentRounds;
